import React from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import type { RichTextComponentProps } from '@/libraries/design/utils/rich-text/src/rich-text';

import { AtlassianIcon } from './atlassian-icon';
import { ErrorIcon } from './error-icon';
import { ProTipIcon } from './pro-tip-icon';

export type PanelType = 'pro-tip' | 'atlassian-pro-tip' | 'highlight';

const iconMap: Record<PanelType, React.ReactElement> = {
	'pro-tip': <ProTipIcon />,
	'atlassian-pro-tip': <AtlassianIcon />,
	highlight: <ErrorIcon />,
};

export interface PanelProps {
	panelType: PanelType;
	label: string;
	description: string | React.ReactElement<RichTextComponentProps>;
}

const addSectionTitleClass = (text: string) =>
	text.replace(/<p><b>([A-Z0-9\s:]+)<\/b><\/p>/g, '<p class="panel-section-title"><b>$1</b></p>');

export const Panel = (props: PanelProps) => {
	const descriptionComponent =
		typeof props.description === 'string' ? (
			<p>{props.description}</p>
		) : React.isValidElement<RichTextComponentProps>(props.description) ? (
			React.cloneElement(props.description, {
				...(props.description.props.text !== undefined
					? { text: addSectionTitleClass(props.description.props.text ?? '') }
					: {}),
			})
		) : (
			(props.description ?? null)
		);
	const icon = iconMap[props.panelType];

	return (
		<div
			css={[
				panelContainerCss,
				css({
					backgroundColor:
						props.panelType === 'atlassian-pro-tip'
							? wacToken('color.extended.blue100')
							: wacToken('color.neutral.light200'),
				}),
			]}
		>
			<div>{icon}</div>
			<div css={textCss}>
				<p className="eyebrow-lg">{props.label}</p>
				{descriptionComponent}
			</div>
		</div>
	);
};

const panelContainerCss = css({
	borderRadius: wacToken('corner.radius.card'),
	paddingBlockStart: token('space.200'),
	paddingBlockEnd: token('space.250'),
	paddingInline: token('space.400'),
	display: 'flex',
	gap: token('space.200'),

	[media.below.sm]: {
		flexDirection: 'column',
		gap: token('space.050'),
	},
});

const textCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['p']: {
		margin: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['p.eyebrow-lg']: {
		marginBlockEnd: token('space.100'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['p.panel-section-title + p']: {
		marginBlockStart: token('space.100'),
	},
});
